/**
 * The Save-to-Notion action, shared by every surface that offers it (SPEC § The Save action).
 *
 * The in-flight set is keyed by **post id and lives above both surfaces**, so a post open in the
 * preview popup and visible in the feed spins in both, and a second click while one is in flight is
 * ignored. Each click is its own request and its own toast: saving several posts back to back is a
 * normal thing to do, and the app keeps no record of what was saved (the brief's no-history rule),
 * so a second click on the same post is a legitimate second page.
 *
 * Readiness is checked against the cached #40 answer before anything is sent — that costs no
 * request — and the server's own `409` opens the same dialog as the backstop when the cache is
 * stale.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotionErrorDetails, NotionSaveResponse, NotionStateResponse } from '@lw/shared';
import { getNotion, saveToNotion } from '../../api/notion';
import { QK } from '../../api/queryKeys';
import { describeError, isApiError } from '../../api/client';
import { useToast } from '../../components';
import { NotionSetupDialog } from './NotionSetupDialog';

/** The readiness answer is worth a minute: it changes only when the user edits the tab. */
const NOTION_STALE_MS = 60_000;

type NotionSaveApi = {
  save: (postId: number) => void;
  isSaving: (postId: number) => boolean;
};

const NotionSaveContext = createContext<NotionSaveApi | null>(null);

/** Surfaces outside a provider (there are none today) simply render an inert button. */
const INERT: NotionSaveApi = { save: () => undefined, isSaving: () => false };

export function useNotionSave(): NotionSaveApi {
  return useContext(NotionSaveContext) ?? INERT;
}

/** `details.reason` of the #47 `409`, when the cached readiness was stale. */
function conflictReason(error: unknown): string | null {
  if (!isApiError(error) || error.status !== 409) return null;
  const details = error.details;
  if (typeof details !== 'object' || details === null) return '';
  const reason = (details as { reason?: unknown }).reason;
  return typeof reason === 'string' ? reason : '';
}

/** `details.kind` of a `notion_error` — decides whether the toast offers Retry or Settings. */
function notionErrorKind(error: unknown): NotionErrorDetails['kind'] | null {
  if (!isApiError(error) || error.code !== 'notion_error') return null;
  const details = error.details;
  if (typeof details !== 'object' || details === null) return null;
  const kind = (details as { kind?: unknown }).kind;
  return typeof kind === 'string' ? (kind as NotionErrorDetails['kind']) : null;
}

export type NotionSaveProviderProps = {
  /** Opens the Settings modal on the Notion tab — owned by the view, per §12.1. */
  onOpenSettings: () => void;
  children: ReactNode;
};

export function NotionSaveProvider({ onOpenSettings, children }: NotionSaveProviderProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  // The ref is the authority and the state is the render copy: a Retry action fired from a toast
  // runs a closure captured several renders ago, and a stale `Set` there would swallow the retry.
  const inFlightRef = useRef<Set<number>>(new Set());
  const [inFlight, setInFlight] = useState<ReadonlySet<number>>(() => new Set());
  const [setupReason, setSetupReason] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);

  // Mounted here so the readiness answer is warm; the check itself reads the cache directly.
  useQuery({
    queryKey: QK.notion,
    queryFn: ({ signal }) => getNotion(signal),
    staleTime: NOTION_STALE_MS,
  });

  const openSetup = useCallback((reason: string | null) => {
    setSetupReason(reason);
    setSetupOpen(true);
  }, []);

  const { mutate } = useMutation<NotionSaveResponse, Error, number>({
    mutationFn: (postId) => saveToNotion(postId),
  });

  const markDone = useCallback((postId: number) => {
    inFlightRef.current.delete(postId);
    setInFlight(new Set(inFlightRef.current));
  }, []);

  const save = useCallback(
    (postId: number) => {
      if (inFlightRef.current.has(postId)) return;

      const ready = queryClient.getQueryData<NotionStateResponse>(QK.notion);
      if (ready !== undefined && !ready.configured) {
        openSetup(ready.readyReason);
        return;
      }

      inFlightRef.current.add(postId);
      setInFlight(new Set(inFlightRef.current));
      mutate(postId, {
        onSuccess: (result) => {
          markDone(postId);
          toast.show({
            tone: 'success',
            message: result.message,
            notes: result.warnings,
            ...(result.pageUrl === null
              ? {}
              : { link: { href: result.pageUrl, label: 'Open in Notion' } }),
          });
        },
        onError: (error) => {
          markDone(postId);

          // The cached readiness was stale — same dialog, no toast.
          const reason = conflictReason(error);
          if (reason !== null) {
            void queryClient.invalidateQueries({ queryKey: QK.notion });
            openSetup(reason.length > 0 ? reason : null);
            return;
          }

          const kind = notionErrorKind(error);
          const wantsSettings = kind === 'auth' || kind === 'not_shared';
          toast.show({
            tone: 'error',
            message: describeError(error),
            actions: wantsSettings
              ? [{ label: 'Settings', onClick: onOpenSettings }]
              : [{ label: 'Retry', onClick: () => save(postId) }],
          });
        },
      });
    },
    [openSetup, mutate, markDone, toast, queryClient, onOpenSettings],
  );

  const api = useMemo<NotionSaveApi>(
    () => ({ save, isSaving: (postId) => inFlight.has(postId) }),
    [save, inFlight],
  );

  return (
    <NotionSaveContext.Provider value={api}>
      {children}
      <NotionSetupDialog
        open={setupOpen}
        reason={setupReason}
        onClose={() => {
          setSetupOpen(false);
        }}
        onOpenSettings={() => {
          setSetupOpen(false);
          onOpenSettings();
        }}
      />
    </NotionSaveContext.Provider>
  );
}
